import { UserRole } from "@/types/openapi-enum";

/**
 * Roles that may manage an order: Manager, and Coordinator, which holds
 * exactly a Manager's permissions (ENG-4216). Mirrors the platform's
 * `MANAGER_ROLE_KEYS`.
 */
export const MANAGER_ROLE_KEYS: readonly UserRole[] = [
  UserRole.Manager,
  UserRole.Coordinator,
];

/**
 * Get user role flags from V5 API format (string array)
 * V5 API returns roles as: ["ADMIN", "MANAGER"]
 */
export const getUserRole = (userRoles?: string[] | null | undefined) => {
  let isOwner = false;
  let isManager = false;
  let isCoordinator = false;
  let isTechnician = false;
  let isEvaluator = false;
  let isAdmin = false;

  if (!userRoles) {
    return {
      isOwner,
      isManager,
      isCoordinator,
      isAdmin,
      isTechnician,
      isEvaluator,
    };
  }

  userRoles.forEach((role: string) => {
    if (role === UserRole.Owner || role === UserRole.Member) isOwner = true;
    if (role === UserRole.Manager) isManager = true;
    // A coordinator holds exactly a manager's permissions (ENG-4216); every gate
    // that admits `isManager` admits `isCoordinator` too.
    if (role === UserRole.Coordinator) isCoordinator = true;
    if (role === UserRole.Admin) isAdmin = true;
    if (role === UserRole.Technician) isTechnician = true;
    // An evaluator holds exactly a technician's permissions; every gate that
    // admits `isTechnician` admits `isEvaluator` too.
    if (role === UserRole.Evaluator) isEvaluator = true;
  });

  return {
    isOwner,
    isManager,
    isCoordinator,
    isAdmin,
    isTechnician,
    isEvaluator,
  };
};

/**
 * Get highest priority role from V5 API format (string array)
 * V5 API returns roles as: ["ADMIN", "MANAGER"]
 */
export const getHighestRole = (roles?: string[] | null | undefined): string => {
  if (!roles || roles.length === 0) {
    return "";
  }
  const priorityOrder: string[] = [
    UserRole.Admin,
    UserRole.Manager,
    UserRole.Coordinator,
    UserRole.Technician,
    UserRole.Evaluator,
    UserRole.Owner,
    UserRole.Member,
  ];
  // Create a copy to avoid mutating the original array
  const sortedRoles = [...roles].sort(
    (a, b) => priorityOrder.indexOf(a) - priorityOrder.indexOf(b),
  );
  return sortedRoles[0] || "";
};

/**
 * The role key a person is shown by next to their name: their default role
 * (the one an admin picks on the user page), so a coordinator who also holds
 * ADMIN reads "Coordinator". Without a default role, the most senior assigned
 * role.
 */
export const getDisplayRole = (user: {
  defaultRole?: string | null;
  roles?: string[] | null;
}): string => user.defaultRole || getHighestRole(user.roles);

export const getUserRoleLabel = (role: string) => {
  let label = role;
  if (!role) {
    return null;
  }
  // change owner to member on FE
  if (role === UserRole.Owner) {
    label = "MEMBER";
  }
  return label?.charAt(0).toUpperCase() + label?.slice(1).toLowerCase();
};

// `getPlaceRoleLabel` moved to `@/libs/place-role-labels` — it lives with the
// label map it reads, and shared UI in `components/` needs it (which must not
// import upward from `features/`).
